import React, { useEffect, useMemo } from "react";
import { useTheme } from "@material-ui/core";
import { Redirect, RouteComponentProps } from "react-router-dom";
import {
  ExperimentState,
  PackagesModuleProps,
  CorePackagesCheckout,
  CorePackagesCheckoutV2,
  PACKAGES_CHECKOUT_HANDOFF_FAILED,
} from "redmond";
import { useUserContext } from "@capone/common";

import {
  usePackagesCheckoutProps,
  usePackagesCheckoutV2Props,
} from "@capone/portal";
import {
  AVAILABLE,
  getExperimentVariant,
  PACKAGES_SHOPPING_API_V2,
} from "../../../../../../cap1-modules/packages-module/src/context/experiments";
import config from "../../../utils/config";
import { colors } from "../../../utils/capone/colors";
import { PATH_PACKAGES } from "../../../utils/urlPaths";
import { trackEvent } from "../../../api/v1/trackEvent";
import App from "../../../../../../cap1-modules/packages-module/src/App";

interface IPackagesModuleProps extends RouteComponentProps {
  language: string;
  experiments?: ExperimentState;
  showCoreHomepage?: boolean;
  core?: CorePackagesCheckout;
  coreV2?: CorePackagesCheckoutV2;
}

const PackagesModule = (props: IPackagesModuleProps) => {
  const {
    language,
    experiments,
    core,
    coreV2,
    showCoreHomepage,
    ...routerProps
  } = props;
  const theme = useTheme();
  const { sessionInfo, logo, isRewardsDisabled } = useUserContext(
    config.TENANT
  );
  const { history } = routerProps;

  const moduleProps: PackagesModuleProps = useMemo(
    () => ({
      colors,
      experiments,
      language,
      theme,
      baseHistory: history,
      clientAssets: {
        sessionInfo,
        logo: <img src={logo?.src} alt={logo?.alt} />,
        isRewardsDisabled,
      },
      isAgentPortal: config.ptIsAgentPortal,
      showCoreHomepage,
      core,
      coreV2,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isRewardsDisabled]
  );

  // eslint-disable-next-line react/jsx-props-no-spreading
  return <App {...moduleProps} />;
};

export const LegacyPackagesBooking = (
  props: React.ComponentProps<typeof PackagesModule>
) => {
  const { experiments } = props;
  const isV2Enabled =
    getExperimentVariant(
      experiments?.experiments,
      PACKAGES_SHOPPING_API_V2
    ) === AVAILABLE;

  const checkoutPropsResultV0 = usePackagesCheckoutProps();
  const checkoutPropsResultV2 = usePackagesCheckoutV2Props();
  const checkoutPropsResult = isV2Enabled
    ? checkoutPropsResultV2
    : checkoutPropsResultV0;

  useEffect(() => {
    if (checkoutPropsResult.status === "error") {
      trackEvent({
        eventName: PACKAGES_CHECKOUT_HANDOFF_FAILED,
        properties: {
          missing_fields: checkoutPropsResult.missingFields,
        },
        encryptedProperties: [],
      });
    }
  }, [checkoutPropsResult]);

  if (checkoutPropsResult.status === "error") {
    return <Redirect to={PATH_PACKAGES} />;
  }

  return (
    <PackagesModule
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...props}
      core={isV2Enabled ? undefined : checkoutPropsResultV0.checkoutProps}
      coreV2={isV2Enabled ? checkoutPropsResultV2.checkoutProps : undefined}
    />
  );
};
